import React, { useEffect, useRef } from "react";
import { CiUser } from "react-icons/ci";
import { PiDotsThreeBold } from "react-icons/pi";
import { useSelector } from "react-redux";
import { ipfsGatewayImage } from "../../utils/ipfsGatewayImage";
import { convertTimestamp } from "../../utils/timeConverter";
import { CommentReplyTypes } from "../../types";
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation,
} from "@apollo/client";
import { DELETE_COMMENT_REPLY } from "../../apollo/operations";

type CommentReplyMenuProps = {
  commentReply?: CommentReplyTypes;
  handleToggle: (index: any) => void;
  openOption: any;
  deleteOption: boolean;
  index: number;
  handleOpenNotice: () => void;
  handleCloseNotice: () => void;
  handleEdit: (index: any) => void;
  setOpenOption: React.Dispatch<React.SetStateAction<null>>;
  refetch: (
    variables?: Partial<OperationVariables> | undefined
  ) => Promise<ApolloQueryResult<any>>;
};
const CommentReplyMenu: React.FC<CommentReplyMenuProps> = ({
  commentReply,
  handleToggle,
  openOption,
  deleteOption,
  index,
  handleCloseNotice,
  handleOpenNotice,
  handleEdit,
  refetch,
  setOpenOption,
}) => {
  const user = useSelector((state: any) => state.user.userDetails);
  const pImage =
    commentReply?.commentBy &&
    commentReply?.commentBy?.ecobookProfile?.profileImage;

  const [deleteReply, { loading }] = useMutation(DELETE_COMMENT_REPLY, {
    onCompleted(data) {
      console.log(data);
      refetch();
    },
    onError(err) {
      console.log(err);
      const _error = err.graphQLErrors[0]?.extensions?.originalError;
      console.log(_error);
    },
  });

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleCloseNotice();
    e.preventDefault();
    deleteReply({
      variables: {
        where: {
          id: commentReply?.id,
        },
      },
    }).then(() => {
      console.log("deleted successfully");
    });
  };

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const ignoreClickOnElements = [
        document.getElementById("editButton"), // Replace "editButton" with the actual ID of your edit button
        // Add other elements to ignore clicks on, if any
      ];

      let isClickInsideIgnoreElements = false;
      for (const element of ignoreClickOnElements) {
        if (element && element.contains(event.target)) {
          isClickInsideIgnoreElements = true;
          break;
        }
      }

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        !isClickInsideIgnoreElements
      ) {
        setOpenOption(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setOpenOption, containerRef]);

  return (
    <div
      className="relative flex w-full justify-between font-Rubik items-center lg:h-8 h-6 lg:mb-2"
      ref={containerRef}
      key={index}
    >
      <div className="flex w-[70%] lg:gap-4 gap-2 h-8 items-center lg:text-base">
        <div className="lg:w-8 lg:h-8 w-6 h-6">
          {pImage ? (
            <img
              src={ipfsGatewayImage(pImage!)}
              alt=""
              className="lg:w-8 lg:h-8 w-6 h-6 rounded-full"
            />
          ) : (
            <div className="lg:w-8 lg:h-8 w-6 h-6  bg-slate-100 flex items-center justify-center rounded-full">
              <CiUser size={15} />
            </div>
          )}
        </div>
        <div className=" text-[#01262F] text-[10px] font-medium">
          {`${commentReply?.commentBy.firstName} ${commentReply?.commentBy.lastName}`}
        </div>
        <div className=" text-[#646464] text-[10px] font-normal">
          {convertTimestamp(commentReply?.createdAt)}
        </div>
      </div>
      {!loading ? (
        <PiDotsThreeBold
          size={22}
          onClick={() => handleToggle(index)}
          className="cursor-pointer"
        />
      ) : (
        loading && <PiDotsThreeBold size={22} className="cursor-pointer" />
      )}
      {openOption === index && (
        <ul className="bg-white flex flex-col w-[99px] transition-all px-3 ease-in-out duration-100 justify-center items-center gap-[10px] absolute right-4 top-3 z-10  py-3 rounded-sm shadow-[1px_-1px_10px_5px_rgba(0,0,0,0.1)] font-semibold font-Poppins text-sm text-[#646464]">
          {user.id === commentReply?.commentBy.id && (
            <button
              id="editButton"
              className="hover:rounded hover:bg-gray-100 py-1 px-3 cursor-pointer"
              onClick={() => handleEdit(index)}
            >
              Edit
            </button>
          )}
          {commentReply?.commentBy.id && (
            <li
              className="hover:rounded hover:bg-gray-100 cursor-pointer py-1 px-2"
              onClick={handleOpenNotice}
            >
              Delete
            </li>
          )}
        </ul>
      )}
      {deleteOption && (
        <div className="fixed inset-0 w-full z-30 align-middle flex justify-center items-center transition-colors overflow-y-hidden bg-black/20">
          <div className="bg-[#FFF] lg:w-[60%] lg:h-[252px] h-[270px] w-[85%] shadow rounded-lg p-8 font-Rubik flex flex-col lg:gap-5">
            <h1 className=" lg:text-darkGreen lg:text-left text-black text-xs font-bold text-center mb-1 lg:mb-0 lg:text-base lg:font-medium">
              Delete comment
            </h1>
            <p className=" lg:text-base text-[10px] text-center lg:text-left text-[#67727E]">
              Are you sure you want to delete this comment? This will remove the
              comment and can’t be undone.
            </p>
            <div className="flex flex-col-reverse lg:flex-row mt-[29px] lg:mt-0 lg:justify-between lg:gap-0 text-[15px] font-Rubik font-medium text-white">
              <div
                className="lg:w-[131px] h-10 lg:h-[48px] border-[0.5] border-solid border-[#08080D] lg:border-0 w-full flex justify-center items-center text-midGreen lg:text-inherit lg:rounded rounded-md shadow lg:bg-[#67727E] text-center cursor-pointer hover:text-darkGreen active:ml-1 active:mt-px transition duration-75"
                onClick={handleCloseNotice}
              >
                NO, CANCEL
              </div>
              <button
                disabled={loading}
                className="lg:w-[131px] w-full h-10 lg:h-[48px] flex justify-center items-center rounded-md mb-4 lg:mb-0 lg:rounded shadow bg-midGreen text-center cursor-pointer hover:text-lightGreen active:bg-white active:ease-linear transition duration-75"
                onClick={handleDelete}
              >
                YES, DELETE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentReplyMenu;
